import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* AmbientLife — sitenin "yaşadığını" hissettiren zarif anlar (Tudi'nin sahne yönetimi).
   Uzun ve rastgele aralıklarla (≈100-220 sn) TEK bir ince an oynatılır, sıra her seferinde değişir:
   1) shimmer — ekrandan süzülen çok hafif altın ışık süpürmesi
   2) kanji   — alttan yükselip kaybolan soluk bir kanji (慈 信 光 和 雅 命)
   3) petals  — kısa bir taç yaprağı esintisi (mevcut sakura animasyonunun ara sıra coşması)
   Hepsi pointer-events: none, son derece hafif opaklık — lüks dokuyu bozmaz, sadece yaşam hissi verir. */

const KANJI = ["慈", "信", "光", "和", "雅", "命", "忍"];

export function AmbientLife() {
  const [moment, setMoment] = useState(null); // { kind, key, x, kanji }
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const kinds = ["shimmer", "kanji", "petals"];
    let last = -1;
    let timer;
    let clearer;
    const schedule = (wait) => {
      timer = setTimeout(() => {
        if (!aliveRef.current) return;
        let k = Math.floor(Math.random() * kinds.length);
        if (k === last) k = (k + 1) % kinds.length; // art arda aynı an olmasın
        last = k;
        const kind = kinds[k];
        setMoment({
          kind,
          key: Date.now(),
          x: 6 + Math.random() * 68,
          kanji: KANJI[Math.floor(Math.random() * KANJI.length)],
        });
        clearer = setTimeout(() => { if (aliveRef.current) setMoment(null); }, kind === "kanji" ? 15000 : kind === "shimmer" ? 7000 : 24000);
        schedule(100000 + Math.random() * 120000); // bir sonraki an ~100-220 sn sonra
      }, wait);
    };
    schedule(30000 + Math.random() * 25000); // ilk an: açılıştan 30-55 sn sonra
    return () => { aliveRef.current = false; clearTimeout(timer); clearTimeout(clearer); };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[4] overflow-hidden" aria-hidden="true" data-testid="ambient-life">
      <AnimatePresence>
        {moment?.kind === "shimmer" && (
          <motion.div
            key={moment.key}
            className="absolute inset-y-0 w-[38vw]"
            style={{
              left: 0,
              background: "linear-gradient(105deg, transparent 0%, rgba(212,175,55,0.05) 35%, rgba(245,231,198,0.10) 50%, rgba(212,175,55,0.05) 65%, transparent 100%)",
              mixBlendMode: "soft-light",
            }}
            initial={{ x: "-45vw" }}
            animate={{ x: "115vw" }}
            exit={{ opacity: 0 }}
            transition={{ duration: 6.5, ease: [0.22, 0.6, 0.3, 1] }}
          />
        )}
        {moment?.kind === "kanji" && (
          <motion.span
            key={moment.key}
            className="absolute bottom-[-8vh] font-light select-none"
            style={{ left: `${moment.x}%`, fontSize: "clamp(72px, 11vw, 150px)", color: "#8A2434" }}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 0.13, 0.13, 0], y: "-108vh" }}
            exit={{ opacity: 0 }}
            transition={{ duration: 15, ease: "linear" }}
          >
            {moment.kanji}
          </motion.span>
        )}
        {moment?.kind === "petals" && (
          <motion.div key={moment.key} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 2.5 }}>
            {Array.from({ length: 14 }, (_, i) => {
              const left = (i * 71 + Math.floor(Math.random() * 9)) % 100;
              const size = 9 + ((i * 5) % 8);
              const dur = 9 + ((i * 11) % 8);
              const delay = -((i * 2.9) % 12);
              const sway = (i % 2 === 0 ? 1 : -1) * (34 + ((i * 13) % 46));
              return (
                <svg key={i} viewBox="0 0 20 20" className="jp-petal"
                  style={{ left: `${left}%`, width: size, animationDuration: `${dur}s`, animationDelay: `${delay}s`, "--sway": `${sway}px` }}>
                  <path d="M10 1 C 15 6 16 12 10 19 C 4 12 5 6 10 1 Z" fill="#F4D7DC" stroke="#E7A9BC" strokeWidth="0.7" />
                </svg>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
